import { Trash2 } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { getErrorMessage } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { useDeleteMyDesign } from '../model/use-delete-my-design';
import { DeleteMyDesignDialog } from './delete-my-design-dialog';

const DELETE_FALLBACK = '디자인을 삭제하지 못했어요. 잠시 후 다시 시도해 주세요.';

interface DeleteMyDesignButtonProps {
  readonly design: { readonly id: string; readonly name: string };
}

/** 카드 이름 줄의 삭제 아이콘 — 확인 창을 거쳐 바로 지운다. 실패 사유는 창 안에 남긴다. */
export const DeleteMyDesignButton = ({ design }: DeleteMyDesignButtonProps): ReactElement => {
  const { remove, isPending } = useDeleteMyDesign();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    setError(null);
    try {
      await remove(design.id);
      setOpen(false);
    } catch (caught) {
      setError(getErrorMessage(caught, DELETE_FALLBACK));
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`${design.name} 삭제`}
        title="삭제"
        className="size-8 rounded-full text-muted-foreground hover:bg-muted hover:text-destructive"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        <Trash2 aria-hidden className="size-4" strokeWidth={1.5} />
      </Button>
      {open ? (
        <DeleteMyDesignDialog
          designName={design.name}
          onConfirm={() => void confirm()}
          onClose={() => setOpen(false)}
          pending={isPending}
          message={error}
        />
      ) : null}
    </>
  );
};
